import { Injectable } from '@angular/core';
import { EnvironmentService } from '@services/environment.service';
import { HttpUtilService } from '@services/http-util.service';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TermsService {
  constructor(
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private notifications: NotificationsService,
    private translationService: TranslationService
  ) {}
  private _integration;
  private _termsAvailableToSelectParams = {
    pageNumber: 0,
    perPage: 10,
    searchParam: 'name',
    searchTerm: '',
    orderBy: 'name',
    direction: 'ASC',
    queryKeys: '',
    queryValues: '',
    queryOperations: 'available',
  };
  private _termsToImport = new BehaviorSubject({
    terms: [],
    isLoading: true,
  });
  private _termsAvailableToSelect = new BehaviorSubject({
    terms: [],
    isInitialLoad: true,
    isLoading: true,
    isProcessing: true,
    count: 0,
  });
  public triggerCall: boolean = false;
  public _termChanges = { original: [], current: [], checkAll: false };
  public hasChanges: boolean = false;

  get integration() {
    return this._integration;
  }

  set integration(integration) {
    this._integration = integration;
    if (!integration) return;
    const terms = integration?.json_data?.termsToPull || [];
    this._termsToImport.next({
      terms,
      isLoading: false,
    });
    this.hasChanges = false;
    this.termChanges = {
      original: [...terms],
      current: [...terms],
      checkAll: false,
    };
    this.termsAvailableToSelectParams = {
      queryKeys: `${integration?.name}_id`,
      queryValues: integration?.id,
    } as any;
  }

  get termsToImport$() {
    return this._termsToImport.asObservable();
  }

  get termChanges() {
    return this._termChanges;
  }

  set termChanges(termChanges) {
    this._termChanges = { ...this._termChanges, ...termChanges };
  }

  get termsAvailableToSelect$() {
    return this._termsAvailableToSelect.asObservable();
  }

  get termsAvailableToSelectParams() {
    return this._termsAvailableToSelectParams;
  }

  set termsAvailableToSelectParams(params) {
    this._termsAvailableToSelect.next({
      ...this._termsAvailableToSelect.getValue(),
      isProcessing: true,
    });
    const updatedParams = {
      ...this._termsAvailableToSelectParams,
      ...params,
    };
    this._termsAvailableToSelectParams = updatedParams;
    if (this.triggerCall) {
      this.triggerCall = false;
      this.getTermsAvailable();
    }
  }

  resetTermsAvailable() {
    this.triggerCall = true;
    this._termsAvailableToSelect.next({
      terms: [],
      isLoading: true,
      count: 0,
      isProcessing: true,
      isInitialLoad: true,
    });
  }

  get queryParams() {
    const {
      pageNumber,
      perPage,
      searchParam,
      searchTerm,
      orderBy,
      direction,
      queryKeys,
      queryValues,
      queryOperations,
    } = this._termsAvailableToSelectParams;
    let queryParams = `mode=list&output=dataonly&key_values=name,start_at,end_at,integration_name,term_id,last_imported&pageNumber=${pageNumber}&perPage=${perPage}&orderBy=${orderBy}&direction=${direction}&queryKeys=${queryKeys}&queryValues=${queryValues}&queryOperations=${queryOperations}`;
    if (searchTerm) {
      queryParams += `&searchTerm=${searchTerm}&searchParam=${searchParam}`;
    }
    return queryParams;
  }

  getNewTermsToBeAdded() {
    return this._termChanges.current.filter(
      term =>
        !this._termChanges.original.find(_term => {
          return term?.id === _term?.id;
        })
    );
  }

  checkForChanges() {
    if (
      this._termChanges?.original?.length !== this._termChanges?.current?.length
    ) {
      this.hasChanges = true;
      return;
    }

    let hasChanges = false;

    for (let i = 0; i < this._termChanges.original.length; i++) {
      const wasFound = !!this._termChanges.current.find(
        term => this._termChanges.original[i].id === term.id
      );
      if (!wasFound) {
        hasChanges = true;
        break;
      }
    }
    this.hasChanges = hasChanges;
  }

  getTermsAvailable() {
    const url = `${this.environmentService.environment.apiRoot}/terms?${this.queryParams}`;
    this.http
      .request('get', url)
      .pipe(catchError(error => of({ error })))
      .pipe(
        map(response => {
          if (
            response &&
            typeof response === 'object' &&
            Object.keys(response).length === 0
          )
            return { listItems: [], totalItems: 0 };
          return response?.id
            ? { listItems: [response], totalItems: 1 }
            : response;
        })
      )
      .subscribe(response => {
        if (!response || response?.error) {
          const text = this.translationService.getTranslatedContent(
            'error_gathering_terms_at_this_time',
            'Error Gathering Terms at this time'
          );
          this.notifications.popNotification('', text);
          this._termsAvailableToSelect.next({
            terms: response.listItems,
            isLoading: false,
            count: 0,
            isProcessing: false,
            isInitialLoad: false,
          });
        }
        if (response?.listItems) {
          this._termsAvailableToSelect.next({
            terms: response.listItems,
            isLoading: false,
            count: response?.totalItems,
            isProcessing: false,
            isInitialLoad: false,
          });

          if (
            this.termChanges.current.length === response?.totalItems &&
            response?.totalItems
          ) {
            this.termChanges.checkAll = true;
          }
        }
      });
  }
}
